/* ────────────────────────────────────────────────────────────────────────────
 * packages/shared/src/money.ts
 *
 * The arithmetic environment for every pay calculation in the system.
 * Spec: docs/spec/11-business-rules.md §0.2 (scales), §0.3 (the Decimal clone),
 *       §1 (the eleven rounding boundaries), §9 (BR-12);
 *       docs/spec/12-api-contract.md §1.3 (the wire contract);
 *       docs/spec/14-theme.md §6.2 (the RM display rule).
 *
 * RULES
 *  1. Money is NEVER a JS number. It is a `Money` (a decimal.js clone instance)
 *     in services and a fixed-scale decimal STRING on the wire.
 *  2. Rounding happens only at the eleven boundaries of §1. `round2` / `round4`
 *     / `round6` / `trunc2` / `ceilToIncrement` are the only rounding calls.
 *  3. Never brand-check a decimal by `constructor.name` — the Prisma client is
 *     minified and reports `'i'`. Use `Money.isDecimal(x)`, which is cross-clone.
 * ──────────────────────────────────────────────────────────────────────────── */
import { Decimal as DecimalJs } from 'decimal.js';
import { MONEY_PATTERN, QUANTITY_PATTERN, FACTOR_PATTERN } from './api-types.js';
/* The three wire regexes of 12-api-contract.md §1.3 are defined once, in
 * api-types.ts, and re-exported here so that `money.ts` is a complete
 * money module. Re-exporting the same declaration (rather than restating the
 * literals) is what keeps `parseMoney` and the API's `@Matches()` decorator
 * provably identical. */
export { MONEY_PATTERN, QUANTITY_PATTERN, FACTOR_PATTERN };
/* ═══════════════════════════ 1. THE CLONE ═══════════════════════════════════ */
/**
 * A dedicated clone so that Prisma's own Decimal global config can never change
 * our precision out from under us, and vice versa.
 *
 * precision 34: numeric(14,4) × numeric(14,4) has at most 28 significant digits;
 * 34 leaves head-room for chained div() in the allocator without ever truncating
 * a value we then round to 2 dp.
 *
 * toExpNeg/toExpPos are widened so `toString()` can never produce `"1e+21"`,
 * which would silently corrupt a canonical hash (11-business-rules.md §14.4).
 */
export const Money = DecimalJs.clone({
    precision: 34,
    rounding: DecimalJs.ROUND_HALF_UP, // the DEFAULT only; every boundary states its own mode
    toExpNeg: -18,
    toExpPos: 30,
    modulo: DecimalJs.ROUND_DOWN,
});
/**
 * Collision-free aliases. `api-types.ts` exports `type Money = string` (the WIRE
 * type), so the package root cannot export both under one name. Import
 * `MoneyDecimal` from `@cropmate/shared`, or `Money` from
 * `@cropmate/shared/money` where the spec snippets use that name.
 */
export const MoneyDecimal = Money;
/**
 * The only sanctioned way to make a `Money`.
 *
 * The `as never` is the spec's own escape hatch (11-business-rules.md §0.3):
 * `Decimal.Value` is nominal to the decimal.js copy that declared it, so a
 * `Prisma.Decimal` — structurally identical, different constructor — is not
 * assignable without it. Passing a foreign Decimal instance is verified to
 * preserve the value exactly (§0.3 [V]).
 */
export const D = (x) => new Money(x);
export const ZERO = D('0');
export const CENT = D('0.01');
/** The BR-12 default increment: ten sen. */
export const TEN_SEN = D('0.10');
/** Cross-clone brand check. NEVER use `constructor.name` — it is `'i'` when the
 *  Prisma client is minified (00-stack.md §2.3). */
export const isDecimal = (x) => Money.isDecimal(x);
/* ═══════════════════════════ 2. ERRORS ══════════════════════════════════════ */
/**
 * Thrown by the pure money helpers. The API maps these onto the error catalogue
 * of 12-api-contract.md §2; `E_CALC_*` codes are internal and surface to clients
 * as `E_INTERNAL` (README §4 conflict #13).
 */
export class MoneyError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = 'MoneyError';
        this.code = code;
    }
}
/* ═══════════════════════════ 3. NEGATIVE ZERO ═══════════════════════════════ */
/**
 * decimal.js keeps a signed zero: `D('-0.01').div('0.10').ceil()` is `-0`, whose
 * `isNegative()` is `true` even though it prints `"0.00"`. Every rounding helper
 * below normalises it away, so a downstream sign test (`W_NEGATIVE_GROSS`,
 * the danger colour in `formatRM`) can never fire on a value that is exactly zero.
 * This changes no emitted string — `toFixed` already prints `"0.00"` for `-0`.
 */
const denormaliseZero = (x) => (x.isZero() ? ZERO : x);
/** Sign test that is correct for negative zero. Prefer this to `.isNegative()`. */
export const isNegativeMoney = (x) => x.lessThan(ZERO);
/* ═══════════════════════════ 4. ROUNDING ════════════════════════════════════ */
/* The rounding-mode vocabulary of §0.3, used verbatim, never a bare number:
 *   ROUND_HALF_UP (4) — half away from zero (Excel ROUND) — every roundN
 *   ROUND_DOWN    (1) — truncate toward zero            — allocator base share
 *   ROUND_CEIL    (2) — toward +infinity                — BR-12 gross rounding
 *   ROUND_UP      (0) — away from zero                  — EPF "up to next ringgit" */
/** 2 dp, half away from zero. Every persisted money AMOUNT ends here. */
export const round2 = (x) => denormaliseZero(x.toDecimalPlaces(2, Money.ROUND_HALF_UP));
/** 4 dp, half away from zero. Money COLUMNS: rateApplied, hourlyRateApplied. */
export const round4 = (x) => denormaliseZero(x.toDecimalPlaces(4, Money.ROUND_HALF_UP));
/** 6 dp, half away from zero. numeric(9,6) FACTOR columns only — never money. */
export const round6 = (x) => denormaliseZero(x.toDecimalPlaces(6, Money.ROUND_HALF_UP));
/** 2 dp, truncated toward zero. The Hamilton allocator's base share (R2/R4). */
export const trunc2 = (x) => denormaliseZero(x.toDecimalPlaces(2, Money.ROUND_DOWN));
/** A persisted money amount must be sen-exact: `value × 100` is an integer.
 *  The three derived RATE fields are the documented exception (§0.2). */
export const isSenExact = (x) => x.times(100).isInteger();
/** Runtime guard, not a comment (§0.2). */
export function assertSenExact(x, what = 'amount') {
    if (!isSenExact(x)) {
        throw new MoneyError('E_CALC_NOT_SEN_EXACT', `${what} ${x.toFixed()} is not sen-exact (value × 100 must be an integer)`);
    }
    return x;
}
/* ═══════════════════════════ 5. BR-12 ═══════════════════════════════════════ */
/**
 * Round `x` UP to the next multiple of `increment`, toward **+infinity**.
 *
 * `.ceil()` on decimal.js is `ROUND_CEIL` — toward +infinity independent of sign.
 * The trailing `toDecimalPlaces(2)` is a SCALE NORMALISATION, not a correction:
 * `ceil()` returns an integer and `× increment` is exact, so the call exists only
 * to give every persisted amount a uniform scale for the canonical hash (§14.4).
 *
 * @throws MoneyError `E_BAD_ROUNDING_INCREMENT` when `increment <= 0` (TV-BR12-14).
 */
export function ceilToIncrement(x, increment) {
    if (increment.lessThanOrEqualTo(ZERO)) {
        throw new MoneyError('E_BAD_ROUNDING_INCREMENT', `rounding increment must be > 0, received ${increment.toFixed()}`);
    }
    return denormaliseZero(x.div(increment).ceil().times(increment).toDecimalPlaces(2, Money.ROUND_HALF_UP));
}
/**
 * **BR-12** — gross is rounded UP to the nearest ten sen, before deductions.
 *
 * NEGATIVE GROSS (11-business-rules.md §9.2, decision **(A)**): the ceiling is
 * *mathematical*, toward +infinity, for every sign. `-12.34 → -12.30` and
 * `-0.01 → 0.00`; it is NOT Excel's `ROUNDUP` (away from zero), which would give
 * `-12.40` / `-0.10`. Reasons, all load-bearing:
 *   1. Intent — BR-12 rounds in the worker's favour; toward +infinity keeps that
 *      for a negative month (penalties exceeding earnings) too.
 *   2. Monotonicity — (A) satisfies `x <= y  =>  f(x) <= f(y)`. Away-from-zero has
 *      a 20-sen discontinuity across zero, so a 2-sen change in raw gross moves
 *      rounded gross by 20 sen.
 *   3. No sign branch, so the engine and any report re-implementation cannot diverge.
 * Clamping a negative gross to zero is NOT offered — it would destroy money
 * silently. `payroll_policy.negative_gross_policy` decides ALLOW_AND_FLAG vs BLOCK.
 */
export const ceilToTenSen = (x) => ceilToIncrement(x, TEN_SEN);
/** BR-12 with the increment from `payroll_policy.gross_rounding_increment`
 *  (default `0.10`; `0.01` after a successful FR-7.1 reconciliation, §9.4). */
export const roundGross = (grossRaw, increment = TEN_SEN) => ceilToIncrement(grossRaw, increment);
/** The BR-12 adjustment is itself a ledger row, so gross always equals the sum
 *  of its parts. Always `>= 0` under decision (A). */
export const roundingAdjustment = (grossRaw, grossRounded) => denormaliseZero(grossRounded.minus(grossRaw));
/* ═══════════════════════════ 6. WIRE ENCODING ═══════════════════════════════ */
/** Outbound money: `numeric(14,4)`, trailing zeros preserved. `"144.0000"`. */
export const toMoneyString = (x) => D(x).toFixed(4);
/** Outbound quantity: `numeric(14,3)`. `"48.000"`. */
export const toQuantityString = (x) => D(x).toFixed(3);
/** Outbound factor: `numeric(9,6)`. `"1.500000"`. */
export const toFactorString = (x) => D(x).toFixed(6);
function parseWith(pattern, domain, code) {
    return (s) => {
        if (typeof s !== 'string' || !pattern.test(s)) {
            throw new MoneyError(code, `${JSON.stringify(s)} is not a valid ${domain} string`);
        }
        return D(s);
    };
}
/** Inbound money. Rejects anything the `numeric(14,4)` column could not store,
 *  including a JS number, `"1e3"`, `"1,234.00"` and 5-dp input. */
export const parseMoney = parseWith(MONEY_PATTERN, 'money', 'E_MONEY_FORMAT');
/** Inbound quantity, `numeric(14,3)`. */
export const parseQuantity = parseWith(QUANTITY_PATTERN, 'quantity', 'E_QUANTITY_FORMAT');
/** Inbound factor, `numeric(9,6)`. */
export const parseFactor = parseWith(FACTOR_PATTERN, 'factor', 'E_FACTOR_FORMAT');
export const isMoneyString = (s) => typeof s === 'string' && MONEY_PATTERN.test(s);
export const isQuantityString = (s) => typeof s === 'string' && QUANTITY_PATTERN.test(s);
export const isFactorString = (s) => typeof s === 'string' && FACTOR_PATTERN.test(s);
/** Non-throwing `parseMoney`. */
export const tryParseMoney = (s) => isMoneyString(s) ? D(s) : null;
/** Exact sum. Never `reduce((a, b) => a + b)` over floats. */
export const sumMoney = (values) => {
    let total = ZERO;
    for (const v of values)
        total = total.plus(D(v));
    return total;
};
/* ═══════════════════════════ 7. DISPLAY ═════════════════════════════════════ */
/** U+00A0 NO-BREAK SPACE, so the amount never wraps away from `RM`. */
const NBSP = ' ';
const EM_DASH = '—';
/** Insert a comma every 3 digits, right to left, integer part only. */
const GROUP = /\B(?=(\d{3})+(?!\d))/g;
const group = (intPart) => intPart.replace(GROUP, ',');
/**
 * The display contract of 14-theme.md §6.2 — the only sanctioned RM renderer.
 *
 *   positive  `RM<NBSP>1,234.56`
 *   zero      `RM<NBSP>0.00`
 *   negative  `(RM<NBSP>1,234.56)`   — parentheses, never a minus sign
 *   absent    `—`
 *
 * Always exactly 2 dp, rounded (never truncated) HALF_UP from the 4-dp wire
 * value, matching the engine's `round2`. The caller adds `--status-danger` for a
 * negative value; the parentheses carry the signal for print and for colour-blind
 * users, so colour is never the only cue.
 *
 * `Number()` / `parseFloat()` are never involved — that is the whole point.
 */
export function formatRM(raw) {
    if (raw === null || raw === undefined || raw === '')
        return EM_DASH;
    const d = D(raw);
    const fixed = d.abs().toFixed(2, Money.ROUND_HALF_UP); // "1234.56"
    const dot = fixed.indexOf('.');
    const body = `RM${NBSP}${group(fixed.slice(0, dot))}.${fixed.slice(dot + 1)}`;
    return isNegativeMoney(d) ? `(${body})` : body;
}
/** `48` · `1,250.5` · `0.125` — no currency, up to 3 dp, trailing zeros trimmed. */
export function formatQuantity(raw) {
    if (raw === null || raw === undefined || raw === '')
        return EM_DASH;
    const s = D(raw).toDecimalPlaces(3, Money.ROUND_HALF_UP).toFixed();
    const dot = s.indexOf('.');
    if (dot === -1)
        return group(s);
    return `${group(s.slice(0, dot))}.${s.slice(dot + 1)}`;
}
//# sourceMappingURL=money.js.map